import type { QueryClient } from '@tanstack/react-query';
import { ID_ESTILO_MODELO } from '@malgoos/shared';
import { ESTILO_PADRAO, marcarSaida } from './estilos';
import { ErroRequisicao } from './api';

/** Rota pública da despedida (Fase 2, W32). */
export const ROTA_SAIDA = '/saida';

export interface DepsSaida {
  /** POST /auth/logout: encerra a sessão no servidor e apaga o cookie. */
  encerrarNoServidor(): Promise<unknown>;
  /** Cache do cliente (React Query) inteiro fora: nada da sessão fica na memória da aba. */
  limparCache(): void;
  /** Navegação com `replace` (o histórico não guarda a última tela do app). */
  navegar(para: string): void;
}

/**
 * Fase 2 (W32): sair. A ordem é a regra de segurança: primeiro a sessão é encerrada no servidor, depois o cache do cliente
 * é limpo e só então a tela de saída aparece (como no antigo, `useAuth.ts:496-522`). Voltar pelo navegador cai numa tela
 * protegida sem cache, que pede a sessão de novo e recebe 401: nenhum dado aparece.
 *
 * 401 conta como sessão já encerrada (expirou ou foi revogada). Outra falha (rede, 5xx, 429) não mostra "Você saiu com
 * segurança", que seria mentira com o cookie ainda válido: devolve `falhou` e a pessoa continua no app com o aviso.
 */
/**
 * Review r23 robustez (O1): prazo do POST de saída. Com o servidor pendurado, a pessoa recebe o aviso de erro em vez de
 * esperar para sempre (o botão fica desligado enquanto o pedido está em voo, `ProvedorSessao.sair`).
 */
export const PRAZO_SAIDA_MS = 15_000;

/**
 * Review r24 spec (M1): cache fora SEM pedido nenhum à API. `qc.clear()` sozinho tira as consultas do cache, mas os
 * observadores das telas ainda montadas (sino, contador, não lidas da Equipe) continuam vivos: no próximo render (o do
 * próprio botão Sair, por exemplo) recriam a consulta vazia e pedem de novo, com a sessão já encerrada. O 401 levava ao
 * /login antes da tela de saída. Aqui cada observador é desligado antes (sem ouvintes nem temporizadores, ele não busca
 * mais ao renderizar, no intervalo nem no foco), o que estava em voo é cancelado e só então o cache é limpo.
 */
export function encerrarConsultas(qc: QueryClient) {
  for (const q of qc.getQueryCache().getAll()) for (const o of [...q.observers]) o.destroy();
  void qc.cancelQueries();
  qc.clear();
}

/** rcor-1 SEG-B2 e rcor-2 SEG2-B1: no aparelho compartilhado, a saída e o login de quem vem depois não levam as cores de quem saiu. */
export function esquecerEstiloDoWorkspace() {
  marcarSaida();
  try {
    if (typeof document !== 'undefined') {
      document.getElementById(ID_ESTILO_MODELO)?.remove();
      document.documentElement.dataset.estilo = ESTILO_PADRAO;
      delete document.documentElement.dataset.acento;
      document.documentElement.style.removeProperty('--fonte-texto');
    }
    for (const k of ['malgoos.estilo', 'malgoos.modelo', 'malgoos.acento', 'malgoos.fonte', 'malgoos.favicon']) localStorage.removeItem(k);
  } catch {
    /* sem storage */
  }
}

export async function encerrarSessao(deps: DepsSaida, prazoMs = PRAZO_SAIDA_MS): Promise<'saiu' | 'falhou'> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const prazo = new Promise<'prazo'>((resolver) => {
    timer = setTimeout(() => resolver('prazo'), prazoMs);
  });
  let concluida = false;
  const concluir = () => {
    if (concluida) return;
    concluida = true;
    esquecerEstiloDoWorkspace();
    deps.limparCache();
    deps.navegar(ROTA_SAIDA);
  };
  const pedido = Promise.resolve().then(() => deps.encerrarNoServidor());
  try {
    if ((await Promise.race([pedido.then(() => 'ok' as const), prazo])) === 'prazo') {
      // Review r24 robustez (O3): a resposta que chega DEPOIS do prazo ainda decide. Encerrada no servidor (ou 401, já
      // encerrada), a tela vai para a saída; antes ficava "não deu para encerrar" com a sessão já morta (próxima chamada 401).
      pedido.then(concluir, (e: unknown) => {
        if (e instanceof ErroRequisicao && e.status === 401) concluir();
      });
      return 'falhou';
    }
  } catch (e) {
    if (!(e instanceof ErroRequisicao && e.status === 401)) return 'falhou';
  } finally {
    clearTimeout(timer);
  }
  concluir();
  return 'saiu';
}
